/**
 * AttachedFilesPreview Component
 * Shows attached files preview above the input area
 * Similar to WhatsApp file attachment preview
 */
import React, { useState } from 'react';
import { 
  X, 
  FileText, 
  Image as ImageIcon, 
  Video, 
  Music, 
  File,
  Paperclip,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { getWorkspaceColors } from './styles';

// File type icons
const FILE_TYPE_ICONS = {
  pdf: FileText,
  image: ImageIcon,
  video: Video,
  audio: Music,
  document: FileText,
  file: File,
};

// File type colors
const FILE_TYPE_COLORS = {
  pdf: '#E53935',
  image: '#43A047',
  video: '#1E88E5',
  audio: '#8E24AA',
  document: '#FB8C00',
  file: '#757575',
};

const AttachedFilesPreview = ({ 
  files = [], 
  onRemoveFile, 
  onClearAll,
}) => {
  const { colors, theme } = useTheme();
  const workspaceColors = getWorkspaceColors(colors, theme === 'dark');
  const [imageErrors, setImageErrors] = useState({});

  if (!files || files.length === 0) return null;

  const styles = {
    container: {
      backgroundColor: workspaceColors.bgTertiary,
      borderTop: `1px solid ${workspaceColors.borderColor}`,
      padding: '10px 16px',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 10,
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 12,
      fontWeight: 600,
      color: workspaceColors.textSecondary,
    },
    clearBtn: {
      padding: '4px 10px',
      border: 'none',
      background: 'transparent',
      color: workspaceColors.danger,
      fontSize: 12,
      fontWeight: 500,
      cursor: 'pointer',
      borderRadius: 6,
      transition: 'background-color 0.15s ease',
    },
    filesList: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 8,
    },
    fileItem: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '6px 10px',
      backgroundColor: workspaceColors.bgPrimary,
      borderRadius: 8,
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: workspaceColors.borderColor,
      maxWidth: 200,
    },
    fileIcon: {
      width: 32,
      height: 32,
      borderRadius: 6,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    fileImagePreview: {
      width: 32,
      height: 32,
      borderRadius: 6,
      objectFit: 'cover',
    },
    fileInfo: {
      flex: 1,
      minWidth: 0,
    },
    fileName: {
      fontSize: 12,
      fontWeight: 500,
      color: workspaceColors.textPrimary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: 100,
    },
    fileType: {
      fontSize: 10,
      color: workspaceColors.textTertiary,
      textTransform: 'uppercase',
    },
    removeBtn: {
      width: 20,
      height: 20,
      border: 'none',
      background: 'transparent',
      color: workspaceColors.textTertiary,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      flexShrink: 0,
      transition: 'all 0.15s ease',
    },
  };

  const handleImageError = (index) => {
    setImageErrors(prev => ({ ...prev, [index]: true }));
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <Paperclip size={14} />
          <span>{files.length} arquivo{files.length > 1 ? 's' : ''} para enviar</span>
        </div>
        {files.length > 1 && (
          <button
            style={styles.clearBtn}
            onClick={onClearAll}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = `${workspaceColors.danger}15`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            Remover todos
          </button>
        )}
      </div>

      {/* Files List */}
      <div style={styles.filesList}>
        {files.map((file, index) => {
          const FileTypeIcon = FILE_TYPE_ICONS[file.type] || File;
          const iconColor = FILE_TYPE_COLORS[file.type] || '#757575';
          const isImage = file.type === 'image';
          const showImagePreview = isImage && !imageErrors[index];

          return (
            <div key={`${file.url}-${index}`} style={styles.fileItem}>
              {/* Icon or Image Preview */}
              {showImagePreview ? (
                <img
                  src={file.url}
                  alt={file.filename}
                  style={styles.fileImagePreview}
                  onError={() => handleImageError(index)}
                />
              ) : (
                <div 
                  style={{
                    ...styles.fileIcon,
                    backgroundColor: `${iconColor}20`,
                  }}
                >
                  <FileTypeIcon size={16} color={iconColor} />
                </div>
              )}
              
              {/* File Info */}
              <div style={styles.fileInfo}>
                <div style={styles.fileName} title={file.filename}>
                  {file.filename}
                </div>
                <div style={styles.fileType}>{file.type}</div>
              </div>
              
              {/* Remove Button */}
              <button
                style={styles.removeBtn}
                onClick={() => onRemoveFile?.(index)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = workspaceColors.danger;
                  e.currentTarget.style.backgroundColor = `${workspaceColors.danger}15`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = workspaceColors.textTertiary;
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                title="Remover arquivo"
              >
                <X size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AttachedFilesPreview;